import cn from 'classnames';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataStatus } from '@features/users/adminPanel/components/DataStatus/DataStatus.tsx';
import formStyles from '@features/users/adminPanel/components/Form/Form.module.scss';
import { Form } from '@features/users/adminPanel/components/Form/Form.tsx';
import { FormValues } from '@features/users/adminPanel/components/Form/Form.types.ts';
import { FormFooter } from '@features/users/adminPanel/components/Form/FormFooter.tsx';
import { FormFields } from '@features/users/adminPanel/components/FormFields/FieldTypes.ts';
import { useAdvertisers } from '@features/users/adminPanel/hooks/useAdvertisers.tsx';
import { Advertiser as IAdvertiser } from '@features/users/adminPanel/types/advertisers.types.ts';
import { InfoPanel } from '@shared/components/InfoPanel/InfoPanel.tsx';
import { InfoPanelTypes } from '@shared/components/InfoPanel/InfoPanel.types.ts';
import { Item } from '@shared/components/Review/Item/Item.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { DataStatuses } from '@shared/types/common.ts';

import styles from './Advertiser.module.scss';

export function Advertiser({ id, name, status, invoicingEmail, vatNumber }: IAdvertiser) {
  const { t } = useTranslation(i18nNameSpace.USERS);
  const statusTranslationKey = status.toLowerCase();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleToggleModalVisibility = () => setIsModalVisible((prevState) => !prevState);

  const { putAdvertiserMutation, putAdvertiserMutationIsPending } = useAdvertisers({
    onSuccessCallback: handleToggleModalVisibility,
    advertiserCollectionQueryEnabled: false,
  });

  const editFormInitialValues = {
    [FormFields.INVOICING_EMAIL]: invoicingEmail,
    [FormFields.VAT_NUMBER]: vatNumber,
  };

  const composedAdvertiserName = name || `-`;
  const showAdvertiserNameInfoPanel = status !== DataStatuses.APPROVED;
  const canBeEdited = status === DataStatuses.IN_APPROVAL;

  const handleEditUserProfileSubmit = (data: FormValues) => {
    putAdvertiserMutation({
      id,
      payload: {
        [FormFields.INVOICING_EMAIL]: data[FormFields.INVOICING_EMAIL],
        [FormFields.VAT_NUMBER]: data[FormFields.VAT_NUMBER],
      },
    });
  };

  return (
    <>
      <section>
        <header className={styles.header}>
          <h3 className={styles.headerText}>{t('page.adminPanel.advertisers.advertiserDetailsHeader')}</h3>
          {canBeEdited && (
            <Button className={styles.editButton} outlined onClick={handleToggleModalVisibility}>
              <i className={cn('pi pi-pencil', styles.pencilIcon)}></i>
              {t('page.adminPanel.common.edit')}
            </Button>
          )}
        </header>

        <div className='grid'>
          <div className='row'>
            <div className='col-12'>
              <Item label={t('page.adminPanel.formFields.advertiserName.label')} value={composedAdvertiserName} />

              {showAdvertiserNameInfoPanel && (
                <InfoPanel type={InfoPanelTypes.INFO} className={styles.infoPanel}>
                  <p>{t('page.adminPanel.infoPanels.advertiserNameStatus')}</p>
                </InfoPanel>
              )}
            </div>
          </div>
          <div className='row'>
            <div className='col-12'>
              <Item label={t('page.adminPanel.formFields.status.label')}>
                <DataStatus variant={status} textLabel={t(`page.adminPanel.dataStatus.${statusTranslationKey}`)} />
              </Item>
            </div>
          </div>
          <div className='row'>
            <div className='col-12'>
              <hr className={styles.divider} />
            </div>
          </div>
          <div className='row'>
            <div className='col-6'>
              <Item label={t('page.adminPanel.formFields.invoicingEmail.label')} value={invoicingEmail} />
            </div>
            <div className='col-6'>
              <Item label={t('page.adminPanel.formFields.vatNumber.label')} value={vatNumber} />
            </div>
          </div>
        </div>
      </section>

      {canBeEdited && (
        <Dialog
          visible={isModalVisible}
          onHide={handleToggleModalVisibility}
          draggable={false}
          resizable={false}
          header={t('page.adminPanel.headers.editAdvertiser')}
        >
          <Form
            submitCallback={handleEditUserProfileSubmit}
            className={formStyles.modalForm}
            initialValues={editFormInitialValues}
          >
            <FormFooter
              isPending={putAdvertiserMutationIsPending}
              dataTestId={'edit-advertiser-submit'}
              cancelCallback={handleToggleModalVisibility}
              submitButtonText={'page.adminPanel.common.save'}
            />
          </Form>
        </Dialog>
      )}
    </>
  );
}
